"use client";

import { Suspense, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useInvoiceStore } from "@/lib/invoice/store";
import { useDrive } from "@/components/Invoice/DriveSync";
import { downloadBlob, uid } from "@/lib/invoice/utils";
import { FY_CHECKLIST, TAX_DOC_TYPES, assessmentYear, fileSize, financialYearOptions, guessFinancialYear, guessTaxDocType, taxDocType } from "@/lib/invoice/taxDocs";
import { Button, Empty, Field, Input, Loading, Modal, PageHeader, Select, Textarea, cx, useFeedback } from "@/components/Invoice/ui";
import { OriginalPreview } from "@/components/Invoice/ImportConfirm";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.doc,.docx,.json,.xml,.zip,.txt";
const typeOptions = TAX_DOC_TYPES.map((t) => ({ value: t.value, label: t.label }));

function TypeChip({ type, className = "" }) {
  const t = taxDocType(type);
  return <span className={cx("inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset", t.tone, className)}>{t.short}</span>;
}

function FileGlyph({ doc }) {
  const ext = (doc.fileName.split(".").pop() || "").slice(0, 4).toUpperCase();
  const t = taxDocType(doc.type);
  return (
    <div className={cx("relative flex h-12 w-10 shrink-0 items-end justify-center rounded-md pb-1 text-[9px] font-bold ring-1 ring-inset", t.tone)} aria-hidden>
      <span className="absolute right-0 top-0 h-3 w-3 rounded-bl-md bg-white/70" />
      {ext || "FILE"}
    </div>
  );
}

function TaxDocumentsInner() {
  const store = useInvoiceStore();
  const drive = useDrive();
  const { toast, confirm } = useFeedback();
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [adding, setAdding] = useState(null); // { rows: [{ key, file, title, type, fy, date }] }
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null); // { doc, url, type }
  const [fyFilter, setFyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [preset, setPreset] = useState(null); // { type, fy } when adding from the checklist

  const docs = useMemo(() => store.documents || [], [store.documents]);
  const years = useMemo(() => [...new Set(docs.map((d) => d.fy).filter(Boolean))].sort().reverse(), [docs]);

  if (!store.ready) return <Loading />;

  const syncOn = !!drive.cfg?.enabled;
  const onDrive = docs.filter((d) => d.driveId).length;
  const totalSize = docs.reduce((s, d) => s + (Number(d.size) || 0), 0);
  const list = docs
    .filter((d) => (!fyFilter || d.fy === fyFilter) && (!typeFilter || d.type === typeFilter))
    .filter((d) => `${d.title} ${d.fileName} ${d.notes || ""} ${d.ackNo || ""} ${taxDocType(d.type).label}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.fy || "").localeCompare(a.fy || "") || TAX_DOC_TYPES.findIndex((t) => t.value === a.type) - TAX_DOC_TYPES.findIndex((t) => t.value === b.type) || (a.title || "").localeCompare(b.title || ""));
  const groups = [...new Set(list.map((d) => d.fy || ""))].map((fy) => ({ fy, docs: list.filter((d) => (d.fy || "") === fy) }));
  // a year's checklist also shows for the selected year when nothing is in it yet
  if (fyFilter && !groups.some((g) => g.fy === fyFilter) && !typeFilter && !q) groups.unshift({ fy: fyFilter, docs: [] });

  // from this device, or downloaded from Google Drive the first time
  const readFile = (doc) => (drive.fetchDocFile ? drive.fetchDocFile(doc) : store.getDocFile(doc));

  const pick = (p = null) => {
    setPreset(p);
    fileRef.current?.click();
  };
  const startAdding = (files, p = preset) => {
    const rows = [...files].map((file) => ({
      key: uid(),
      file,
      title: file.name.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim(),
      type: p?.type || guessTaxDocType(file.name),
      fy: p?.fy || guessFinancialYear(file.name),
      date: "",
    }));
    if (rows.length) setAdding({ rows });
    setPreset(null);
  };

  const saveAdding = async () => {
    setBusy("Saving…");
    try {
      const out = [];
      for (const r of adding.rows) {
        const id = uid();
        const { encrypted, size } = await store.putDocFile(id, r.file);
        out.push({ id, title: r.title || r.file.name, fileName: r.file.name, mime: r.file.type || "", size, type: r.type, fy: r.fy, date: r.date, encrypted, driveId: "" });
      }
      store.saveDocuments(out);
      setAdding(null);
      toast(`Saved ${out.length} document${out.length > 1 ? "s" : ""}${syncOn ? " – backing up to Google Drive" : ""}`, { tone: "success" });
    } catch (e) {
      toast(`Couldn’t save: ${e.message}`, { tone: "error" });
    } finally {
      setBusy("");
    }
  };

  const openDoc = async (doc, mode = "view") => {
    setBusy(`Opening ${doc.title}…`);
    try {
      const blob = await readFile(doc);
      if (!blob) throw new Error(doc.driveId ? "Connect Google Drive sync to open it on this device." : "This file was added on another device and hasn’t been backed up to Google Drive yet.");
      if (mode === "download") return downloadBlob(blob, doc.fileName || doc.title);
      const file = new File([blob], doc.fileName || doc.title, { type: doc.mime || blob.type });
      const kind = /pdf/.test(doc.mime) || /\.pdf$/i.test(doc.fileName) ? "pdf" : /^image\//.test(doc.mime) ? "image" : "other";
      // PDFs and images are drawn page by page (works in every browser, incl. iPhone)
      setViewing({ doc, kind, preview: { file, source: doc.fileName, items: [] } });
    } catch (e) {
      toast(e.message, { tone: "error" });
    } finally {
      setBusy("");
    }
  };

  const remove = async (doc) => {
    if (!(await confirm(`Delete “${doc.title}”? It goes to the recycle bin (Settings → Recycle bin) for 30 days.`, { okLabel: "Delete", danger: true }))) return;
    const tid = store.deleteDocuments([doc.id]);
    setEditing(null);
    toast("Document deleted", { action: { label: "Undo", onClick: () => store.restoreTrash(tid) } });
  };

  const zipYear = async (fy) => {
    const items = docs.filter((d) => d.fy === fy);
    if (!items.length) return;
    setBusy(`Preparing FY ${fy}…`);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const missing = [];
      const used = new Set();
      for (const d of items) {
        const blob = await readFile(d).catch(() => null);
        if (!blob) {
          missing.push(d.title);
          continue;
        }
        let path = `${taxDocType(d.type).short.replace(/[\\/]/g, "-")}/${d.fileName || d.title}`;
        for (let k = 2; used.has(path); k++) path = path.replace(/(\.[^.]+)?$/, ` (${k})$1`);
        used.add(path);
        zip.file(path, blob);
      }
      downloadBlob(await zip.generateAsync({ type: "blob" }), `Tax documents FY ${fy} (AY ${assessmentYear(fy)}).zip`);
      toast(missing.length ? `Downloaded – ${missing.length} file(s) weren’t available on this device` : `Downloaded all ${items.length} documents for FY ${fy}`, { tone: missing.length ? "error" : "success" });
    } catch (e) {
      toast(e.message, { tone: "error" });
    } finally {
      setBusy("");
    }
  };

  return (
    <div
      onDragOver={(e) => {
        if ([...(e.dataTransfer?.types || [])].includes("Files")) {
          e.preventDefault();
          setDrag(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (e.dataTransfer.files?.length) startAdding(e.dataTransfer.files, null);
      }}
    >
      <PageHeader
        eyebrow="Records"
        title="Tax documents"
        subtitle="ITR, computation, profit & loss, balance sheet, GST returns, 26AS / AIS, TDS certificates and notices – kept by financial year, ready to send to your CA."
        actions={
          <>
            {fyFilter && docs.some((d) => d.fy === fyFilter) && (
              <Button variant="secondary" onClick={() => zipYear(fyFilter)} disabled={!!busy}>
                Download FY {fyFilter} (ZIP)
              </Button>
            )}
            <Button onClick={() => pick()} data-testid="add-docs">
              + Add documents
            </Button>
          </>
        }
      />
      <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="docs-file" onChange={(e) => startAdding(e.target.files || [])} onClick={(e) => (e.target.value = "")} />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["Documents", docs.length, docs.length ? `${years.length} financial year${years.length === 1 ? "" : "s"}` : "Nothing stored yet"],
          ["Storage", fileSize(totalSize), "on this device"],
          ["Google Drive", syncOn ? `${onDrive} / ${docs.length}` : "Off", syncOn ? (onDrive === docs.length ? "All backed up" : "Backing up…") : "Turn on sync to back up"],
          ["Privacy", store.loginEnabled ? "Encrypted" : "Not encrypted", store.loginEnabled ? "With your login password" : "Set up login to encrypt"],
        ].map(([l, v, sub]) => (
          <div key={l} className="rounded-xl bg-white px-4 py-3 shadow-xs ring-1 ring-slate-200/80">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{l}</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-slate-900">{v}</div>
            <div className="mt-0.5 text-xs text-slate-500">{sub}</div>
          </div>
        ))}
      </div>

      {(!syncOn || !store.loginEnabled) && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="docs-safety">
          <span>
            {!syncOn && <b>Files are only on this device. </b>}
            {!syncOn ? "Turn on Google Drive sync so they’re backed up and open on your other devices. " : ""}
            {!store.loginEnabled ? "Set up a login so documents are encrypted before they’re stored or uploaded." : ""}
          </span>
          <Link href={!syncOn ? "/invoice/settings?tab=data" : "/invoice/settings?tab=security"} className="ml-auto rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 ring-1 ring-amber-300 hover:bg-amber-100">
            {!syncOn ? "Set up Drive sync" : "Set up login"}
          </Link>
        </div>
      )}

      {docs.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5" data-testid="fy-chips">
            {["", ...years].map((y) => (
              <button
                key={y || "all"}
                onClick={() => setFyFilter(y)}
                className={cx("rounded-full border px-3 py-1 text-xs font-medium transition", fyFilter === y ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}
              >
                {y ? `FY ${y}` : "All years"}
                {y && <span className={cx("ml-1", fyFilter === y ? "text-white/70" : "text-slate-400")}>{docs.filter((d) => d.fy === y).length}</span>}
              </button>
            ))}
          </div>
          <Select className="max-w-[220px]" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={[{ value: "", label: "All types" }, ...typeOptions]} />
          <Input className="max-w-xs" placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      )}

      {docs.length === 0 ? (
        <button type="button" onClick={() => pick()} className={cx("block w-full rounded-2xl border-2 border-dashed bg-white px-6 py-16 text-center transition", drag ? "border-brand-500 bg-brand-50" : "border-slate-300 hover:border-brand-400")}>
          <div className="text-base font-semibold text-slate-900">Drop your tax documents here, or click to choose</div>
          <div className="mx-auto mt-1 max-w-lg text-sm text-slate-500">PDF, scans, photos, Excel… The type (ITR, P&L, balance sheet, GST return…) and financial year are picked up from the file name – you can change them before saving.</div>
        </button>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => {
            const have = new Set(g.docs.map((d) => d.type));
            return (
              <section key={g.fy || "none"} className="rounded-2xl border border-slate-200/80 bg-white shadow-card" data-testid="fy-group">
                <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                  <div>
                    <div className="text-[15px] font-semibold tracking-tight text-slate-900">{g.fy ? `FY ${g.fy}` : "No financial year"}</div>
                    {g.fy && <div className="text-xs text-slate-500">Assessment year {assessmentYear(g.fy)} · {g.docs.length} document{g.docs.length === 1 ? "" : "s"}</div>}
                  </div>
                  {g.fy && (
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="fy-checklist">
                      {FY_CHECKLIST.map((t) =>
                        have.has(t) ? (
                          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                            ✓ {taxDocType(t).short}
                          </span>
                        ) : (
                          <button key={t} onClick={() => pick({ type: t, fy: g.fy })} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:border-brand-400 hover:text-brand-700" title={`Add the ${taxDocType(t).label} for FY ${g.fy}`}>
                            + {taxDocType(t).short}
                          </button>
                        )
                      )}
                      {g.docs.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => zipYear(g.fy)} disabled={!!busy}>
                          ZIP
                        </Button>
                      )}
                    </div>
                  )}
                </header>
                {g.docs.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-slate-500">Nothing for this year yet – use the + buttons above to add what’s missing.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {g.docs.map((d) => (
                      <li key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50/70" data-testid="doc-row">
                        <FileGlyph doc={d} />
                        <button className="min-w-0 flex-1 text-left" onClick={() => openDoc(d)}>
                          <div className="truncate font-medium text-slate-900 hover:text-brand-700">{d.title || d.fileName}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                            <TypeChip type={d.type} />
                            {d.date && <span>{new Date(d.date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
                            {d.ackNo && <span>· Ack {d.ackNo}</span>}
                            <span>· {fileSize(d.size)}</span>
                            {d.encrypted && <span title="Encrypted with your login">· 🔒</span>}
                            <span className={d.driveId ? "text-emerald-700" : "text-amber-700"} title={d.driveId ? "Backed up to your Google Drive" : "Only on the device it was added on"}>
                              · {d.driveId ? "On Drive" : syncOn ? "Uploading…" : "This device only"}
                            </span>
                          </div>
                          {d.notes && <div className="mt-0.5 truncate text-xs text-slate-500">{d.notes}</div>}
                        </button>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="secondary" onClick={() => openDoc(d)}>
                            View
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openDoc(d, "download")} title="Download">
                            ⤓
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ ...d })}>
                            Edit
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
          {groups.length === 0 && <Empty title="No documents match">Try another year, type or search.</Empty>}
        </div>
      )}

      {drag && docs.length > 0 && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-brand-600/10 backdrop-blur-[1px]">
          <div className="rounded-2xl bg-white px-6 py-4 text-sm font-semibold text-brand-700 shadow-pop ring-1 ring-brand-200">Drop to add tax documents</div>
        </div>
      )}

      {busy && <div className="fixed bottom-4 right-4 z-50 rounded-lg bg-ink-900 px-3 py-2 text-xs text-white shadow-pop">{busy}</div>}

      {/* add: confirm type / year for each file */}
      <Modal
        open={!!adding}
        xwide
        onClose={() => setAdding(null)}
        title={`Add ${adding?.rows.length || 0} document${adding?.rows.length === 1 ? "" : "s"}`}
        footer={
          <>
            <span className="mr-auto hidden text-xs text-slate-500 sm:block">{store.loginEnabled ? "🔒 Encrypted with your login before saving" : "Tip: set up a login to encrypt documents"}</span>
            <Button variant="secondary" onClick={() => setAdding(null)}>
              Cancel
            </Button>
            <Button onClick={saveAdding} disabled={!!busy} data-testid="docs-save">
              Save {adding?.rows.length} document{adding?.rows.length === 1 ? "" : "s"}
            </Button>
          </>
        }
      >
        {adding && (
          <div className="space-y-3" data-testid="docs-add">
            <p className="text-sm text-slate-600">Type and financial year were picked up from the file names – check them and save.</p>
            {adding.rows.map((r) => {
              const set = (patch) => setAdding((a) => ({ rows: a.rows.map((x) => (x.key === r.key ? { ...x, ...patch } : x)) }));
              return (
                <div key={r.key} className="grid grid-cols-1 gap-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200/70 sm:grid-cols-[1.4fr_1.5fr_0.9fr_0.9fr_auto]" data-testid="docs-add-row">
                  <div className="min-w-0">
                    <Input value={r.title} onChange={(e) => set({ title: e.target.value })} aria-label="Title" />
                    <div className="mt-1 truncate text-[11px] text-slate-500">
                      {r.file.name} · {fileSize(r.file.size)}
                    </div>
                  </div>
                  <Select value={r.type} onChange={(e) => set({ type: e.target.value })} options={typeOptions} aria-label="Type" data-testid="docs-add-type" />
                  <Select value={r.fy} onChange={(e) => set({ fy: e.target.value })} options={financialYearOptions().map((o) => ({ value: o.value, label: `FY ${o.value}` }))} aria-label="Financial year" data-testid="docs-add-fy" />
                  <Input type="date" value={r.date} onChange={(e) => set({ date: e.target.value })} aria-label="Date" title="Filing / document date (optional)" />
                  <button className="self-start px-2 py-2 text-slate-400 hover:text-brand-600" title="Remove" onClick={() => setAdding((a) => (a.rows.length > 1 ? { rows: a.rows.filter((x) => x.key !== r.key) } : null))}>
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* edit details */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Document details"
        footer={
          <>
            <Button variant="danger" className="mr-auto" onClick={() => remove(editing)}>
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                store.saveDocument(editing);
                setEditing(null);
                toast("Saved", { tone: "success" });
              }}
            >
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Title" className="sm:col-span-2">
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </Field>
            <Field label="Type">
              <Select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} options={typeOptions} />
            </Field>
            <Field label="Financial year">
              <Select value={editing.fy} onChange={(e) => setEditing({ ...editing, fy: e.target.value })} options={financialYearOptions().map((o) => ({ value: o.value, label: o.label }))} />
            </Field>
            <Field label="Date filed / issued">
              <Input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
            </Field>
            <Field label="Acknowledgement / reference no.">
              <Input value={editing.ackNo || ""} onChange={(e) => setEditing({ ...editing, ackNo: e.target.value })} placeholder="e.g. ITR ack. number" />
            </Field>
            <Field label="Notes" className="sm:col-span-2">
              <Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="e.g. revised return, filed by CA, refund received…" />
            </Field>
            <div className="text-xs text-slate-500 sm:col-span-2">
              {editing.fileName} · {fileSize(editing.size)} · {editing.encrypted ? "🔒 encrypted" : "not encrypted"} · {editing.driveId ? "backed up to Google Drive" : "this device only"}
            </div>
          </div>
        )}
      </Modal>

      {/* viewer */}
      <Modal
        open={!!viewing}
        xwide
        onClose={() => setViewing(null)}
        title={viewing ? <span className="flex items-center gap-2">{viewing.doc.title} <TypeChip type={viewing.doc.type} /></span> : ""}
        footer={
          <>
            <Button variant="secondary" onClick={() => setViewing(null)}>
              Close
            </Button>
            <Button onClick={() => openDoc(viewing.doc, "download")}>Download</Button>
          </>
        }
      >
        {viewing && (
          <div className="paper-solid h-[65vh] overflow-hidden rounded-xl bg-slate-100" data-testid="doc-viewer">
            {viewing.kind !== "other" ? (
              <OriginalPreview p={viewing.preview} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-slate-600">
                <div>This file type can’t be previewed here.</div>
                <Button onClick={() => openDoc(viewing.doc, "download")}>Download {viewing.doc.fileName}</Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function TaxDocumentsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <TaxDocumentsInner />
    </Suspense>
  );
}
